import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { confirmDialog } from 'primereact/confirmdialog';
import api from '../../services/api';

const CLOUD_NAME = 'dtxar5b36';
const UPLOAD_PRESET = 'bordergarita_uploads';
const UPLOAD_FOLDER = 'stories';

async function uploadStoryImage(file) {
  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', UPLOAD_PRESET);
  form.append('folder', UPLOAD_FOLDER);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: 'POST', body: form }
  );
  const json = await res.json();
  if (!res.ok || !json.secure_url) {
    throw new Error(json.error?.message || 'Cloudinary upload failed');
  }
  return json.secure_url;
}

function formatAge(value) {
  if (!value) return '';
  try {
    const ms = Date.now() - new Date(value).getTime();
    const h = Math.floor(ms / 3600000);
    if (h < 1) return 'Ahora';
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  } catch {
    return '';
  }
}

function PersonRow({ person, showLiked }) {
  return (
    <li className="chat-stories-person">
      <span className="chat-stories-person-avatar">
        {person.photoUrl ? (
          <img src={person.photoUrl} alt="" />
        ) : (
          <span>{(person.name || '?').slice(0, 1).toUpperCase()}</span>
        )}
        {showLiked && person.liked ? (
          <i className="pi pi-heart-fill chat-stories-person-heart" />
        ) : null}
      </span>
      <span className="chat-stories-person-meta">
        <strong>{person.name || 'Usuario'}</strong>
        {person.synthetic ? (
          <em>Impulso</em>
        ) : null}
      </span>
    </li>
  );
}

/**
 * Stories strip for the selected city — sits under the direction switch
 * like StoriesRow in the Flutter chat module.
 */
export default function ChatStoriesPanel({
  cityKey,
  cityLabel,
  toast,
  embedded = false,
}) {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [viewerGroup, setViewerGroup] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadText, setUploadText] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [boostLikes, setBoostLikes] = useState(0);
  const [boostViews, setBoostViews] = useState(0);
  const [savingBoost, setSavingBoost] = useState(false);
  const [activity, setActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const load = useCallback(async () => {
    if (!cityKey) {
      setGroups([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.get('/stories', {
        params: { cityKey },
      });
      setGroups(Array.isArray(data?.groups) ? data.groups : []);
    } catch {
      toast?.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las stories',
      });
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [cityKey, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (uploadPreview) URL.revokeObjectURL(uploadPreview);
    };
  }, [uploadPreview]);

  const currentStory = viewerGroup?.stories?.[viewerIndex] || null;

  const loadActivity = useCallback(
    async (storyId) => {
      if (!storyId) {
        setActivity(null);
        return;
      }
      try {
        setLoadingActivity(true);
        const { data } = await api.get(`/stories/${storyId}/activity`);
        setActivity(data);
      } catch {
        setActivity(null);
        toast?.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar vistos / likes',
        });
      } finally {
        setLoadingActivity(false);
      }
    },
    [toast]
  );

  const openGroup = (group) => {
    if (!group?.stories?.length) return;
    setViewerGroup(group);
    setViewerIndex(0);
    const first = group.stories[0];
    setBoostLikes(Number(first?.boostLikes) || 0);
    setBoostViews(Number(first?.boostViews) || 0);
    loadActivity(first?.id);
  };

  const closeViewer = () => {
    setViewerGroup(null);
    setViewerIndex(0);
    setActivity(null);
  };

  useEffect(() => {
    if (!currentStory) return;
    setBoostLikes(Number(currentStory.boostLikes) || 0);
    setBoostViews(Number(currentStory.boostViews) || 0);
    loadActivity(currentStory.id);
  }, [currentStory?.id, loadActivity]);

  const goStory = (nextIndex) => {
    setViewerIndex(nextIndex);
  };

  const patchStoryInGroups = (updated) => {
    if (!updated?.id) return;
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        stories: (group.stories || []).map((s) =>
          s.id === updated.id ? { ...s, ...updated } : s
        ),
      }))
    );
    setViewerGroup((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        stories: (prev.stories || []).map((s) =>
          s.id === updated.id ? { ...s, ...updated } : s
        ),
      };
    });
  };

  const saveBoost = async () => {
    if (!currentStory?.id) return;
    try {
      setSavingBoost(true);
      const { data } = await api.patch(`/stories/${currentStory.id}/boost`, {
        boostLikes: Math.max(0, Math.floor(Number(boostLikes) || 0)),
        boostViews: Math.max(0, Math.floor(Number(boostViews) || 0)),
      });
      patchStoryInGroups(data);
      await loadActivity(currentStory.id);
      toast?.current?.show({
        severity: 'success',
        summary: 'Listo',
        detail: 'Engagement actualizado (la app solo ve el total)',
      });
    } catch {
      toast?.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo guardar el impulso',
      });
    } finally {
      setSavingBoost(false);
    }
  };

  const deleteStory = (story) => {
    if (!story?.id) return;
    confirmDialog({
      message: '¿Borrar esta story? No se puede deshacer.',
      header: 'Borrar story',
      icon: 'pi pi-trash',
      acceptLabel: 'Borrar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.delete(`/stories/${story.id}`);
          toast?.current?.show({
            severity: 'success',
            summary: 'Listo',
            detail: 'Story borrada',
          });
          if (viewerGroup) {
            const nextStories = viewerGroup.stories.filter(
              (s) => s.id !== story.id
            );
            if (nextStories.length === 0) {
              closeViewer();
            } else {
              setViewerGroup({ ...viewerGroup, stories: nextStories });
              setViewerIndex((i) => Math.min(i, nextStories.length - 1));
            }
          }
          load();
        } catch {
          toast?.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo borrar la story',
          });
        }
      },
    });
  };

  const onPickFile = (file) => {
    if (!file) return;
    if (uploadPreview) URL.revokeObjectURL(uploadPreview);
    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
  };

  const publishComunicado = async () => {
    if (!cityKey || !uploadFile) {
      toast?.current?.show({
        severity: 'warn',
        summary: 'Falta imagen',
        detail: 'Elige una imagen para el comunicado',
      });
      return;
    }
    try {
      setPublishing(true);
      const imageUrl = await uploadStoryImage(uploadFile);
      await api.post('/stories', {
        cityKey,
        imageUrl,
        text: uploadText.trim(),
      });
      toast?.current?.show({
        severity: 'success',
        summary: 'Publicado',
        detail: 'Comunicado activo 24h en la app',
      });
      setUploadOpen(false);
      setUploadText('');
      setUploadFile(null);
      if (uploadPreview) URL.revokeObjectURL(uploadPreview);
      setUploadPreview('');
      load();
    } catch (err) {
      toast?.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.message || 'No se pudo publicar el comunicado',
      });
    } finally {
      setPublishing(false);
    }
  };

  if (!cityKey) return null;

  const viewers = activity?.viewers || [];
  const likers = activity?.likers || [];

  return (
    <div className={embedded ? 'chat-stories chat-stories-embedded' : 'chat-stories'}>
      <div className="chat-stories-toolbar">
        <div>
          <p className="chat-stories-city">Stories</p>
          {!embedded ? (
            <p className="chat-stories-hint">
              {cityLabel || cityKey} · mismas que la app
            </p>
          ) : null}
        </div>
        <div className="chat-stories-actions">
          <Button
            type="button"
            label="Actualizar"
            icon="pi pi-refresh"
            text
            size="small"
            onClick={load}
          />
          <Button
            type="button"
            label="Comunicado"
            icon="pi pi-megaphone"
            size="small"
            onClick={() => setUploadOpen(true)}
          />
        </div>
      </div>

      {loading ? (
        <div className="chat-stories-loading">
          <ProgressSpinner style={{ width: 28, height: 28 }} />
        </div>
      ) : groups.length === 0 ? (
        <p className="chat-mod-empty chat-stories-empty-inline">
          Sin stories activas. Publica un comunicado o espera a la comunidad.
        </p>
      ) : (
        <div className="chat-stories-strip" role="list">
          {groups.map((group) => {
            const cover = group.stories?.[group.stories.length - 1];
            const isComunicado = group.kind === 'comunicado';
            return (
              <button
                key={`${group.kind}:${group.crossingName}`}
                type="button"
                className="chat-stories-circle"
                onClick={() => openGroup(group)}
              >
                <span
                  className={
                    isComunicado
                      ? 'chat-stories-ring comunicado'
                      : 'chat-stories-ring'
                  }
                >
                  {cover?.imageUrl ? (
                    <img src={cover.imageUrl} alt="" />
                  ) : (
                    <span className="chat-stories-placeholder">
                      <i className="pi pi-image" />
                    </span>
                  )}
                </span>
                <span className="chat-stories-label">
                  {isComunicado ? 'Comunicados' : group.crossingName}
                </span>
                <span className="chat-stories-count">
                  {group.stories?.length || 0}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <Dialog
        header={
          viewerGroup
            ? viewerGroup.kind === 'comunicado'
              ? 'Comunicados'
              : viewerGroup.crossingName
            : 'Story'
        }
        visible={Boolean(viewerGroup)}
        onHide={closeViewer}
        className="chat-stories-viewer-dialog"
        style={{ width: 'min(520px, 96vw)' }}
        maximizable
      >
        {currentStory ? (
          <div className="chat-stories-viewer">
            <div className="chat-stories-progress">
              {(viewerGroup.stories || []).map((s, i) => (
                <span
                  key={s.id}
                  className={
                    i <= viewerIndex
                      ? 'chat-stories-bar on'
                      : 'chat-stories-bar'
                  }
                />
              ))}
            </div>
            <div className="chat-stories-viewer-meta">
              <div>
                <strong>{currentStory.authorName || '—'}</strong>
                <span>{formatAge(currentStory.createdAt)}</span>
              </div>
              <div className="chat-stories-viewer-stats">
                <span title="Likes totales">
                  <i className="pi pi-heart" /> {currentStory.likeCount || 0}
                </span>
                <span title="Vistas totales">
                  <i className="pi pi-eye" /> {currentStory.viewCount || 0}
                </span>
              </div>
            </div>
            <div className="chat-stories-viewer-image">
              <img src={currentStory.imageUrl} alt="" />
              {currentStory.text ? (
                <p className="chat-stories-viewer-text">{currentStory.text}</p>
              ) : null}
            </div>

            <TabView className="chat-stories-activity-tabs">
              <TabPanel
                header={`Vistos (${viewers.length})`}
                leftIcon="pi pi-eye mr-2"
              >
                {loadingActivity ? (
                  <div className="chat-stories-loading">
                    <ProgressSpinner style={{ width: 28, height: 28 }} />
                  </div>
                ) : viewers.length === 0 ? (
                  <p className="chat-mod-empty">Nadie ha visto esta story.</p>
                ) : (
                  <ul className="chat-stories-people">
                    {viewers.map((person) => (
                      <PersonRow
                        key={person.userId}
                        person={person}
                        showLiked
                      />
                    ))}
                  </ul>
                )}
              </TabPanel>
              <TabPanel
                header={`Likes (${likers.length})`}
                leftIcon="pi pi-heart mr-2"
              >
                {loadingActivity ? (
                  <div className="chat-stories-loading">
                    <ProgressSpinner style={{ width: 28, height: 28 }} />
                  </div>
                ) : likers.length === 0 ? (
                  <p className="chat-mod-empty">Nadie ha dado like todavía.</p>
                ) : (
                  <ul className="chat-stories-people">
                    {likers.map((person) => (
                      <PersonRow
                        key={person.userId}
                        person={person}
                        showLiked={false}
                      />
                    ))}
                  </ul>
                )}
              </TabPanel>
            </TabView>

            <div className="chat-stories-boost">
              <p className="chat-stories-boost-title">
                Impulso de engagement
              </p>
              <p className="chat-stories-hint">
                Se suma a likes/vistas reales. La app solo ve el total.
              </p>
              <div className="chat-stories-boost-row">
                <label>
                  Likes extra
                  <InputNumber
                    value={boostLikes}
                    onValueChange={(e) => setBoostLikes(e.value ?? 0)}
                    min={0}
                    max={50000}
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClassName="p-button-secondary"
                    incrementButtonClassName="p-button-secondary"
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                  />
                  <span className="chat-stories-boost-meta">
                    reales {currentStory.realLikeCount ?? 0}
                  </span>
                </label>
                <label>
                  Vistas extra
                  <InputNumber
                    value={boostViews}
                    onValueChange={(e) => setBoostViews(e.value ?? 0)}
                    min={0}
                    max={50000}
                    showButtons
                    buttonLayout="horizontal"
                    decrementButtonClassName="p-button-secondary"
                    incrementButtonClassName="p-button-secondary"
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                  />
                  <span className="chat-stories-boost-meta">
                    reales {currentStory.realViewCount ?? 0}
                  </span>
                </label>
                <Button
                  type="button"
                  label={savingBoost ? 'Guardando…' : 'Aplicar'}
                  icon="pi pi-check"
                  size="small"
                  loading={savingBoost}
                  onClick={saveBoost}
                />
              </div>
            </div>
            <div className="chat-stories-viewer-nav">
              <Button
                type="button"
                icon="pi pi-chevron-left"
                text
                disabled={viewerIndex <= 0}
                onClick={() => goStory(Math.max(0, viewerIndex - 1))}
              />
              <Button
                type="button"
                label="Borrar"
                icon="pi pi-trash"
                severity="danger"
                text
                onClick={() => deleteStory(currentStory)}
              />
              <Button
                type="button"
                icon="pi pi-chevron-right"
                text
                disabled={
                  viewerIndex >= (viewerGroup.stories?.length || 1) - 1
                }
                onClick={() =>
                  goStory(
                    Math.min((viewerGroup.stories?.length || 1) - 1, viewerIndex + 1)
                  )
                }
              />
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        header="Subir comunicado"
        visible={uploadOpen}
        onHide={() => {
          if (publishing) return;
          setUploadOpen(false);
        }}
        style={{ width: 'min(440px, 96vw)' }}
        footer={
          <div className="chat-stories-upload-footer">
            <Button
              type="button"
              label="Cancelar"
              text
              disabled={publishing}
              onClick={() => setUploadOpen(false)}
            />
            <Button
              type="button"
              label={publishing ? 'Publicando…' : 'Publicar'}
              icon="pi pi-send"
              loading={publishing}
              onClick={publishComunicado}
            />
          </div>
        }
      >
        <p className="chat-stories-hint">
          Sale en la app como categoría <strong>Comunicados</strong> para{' '}
          {cityLabel || cityKey}. Expira en 24 horas.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => onPickFile(e.target.files?.[0])}
        />
        <button
          type="button"
          className="chat-stories-drop"
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadPreview ? (
            <img src={uploadPreview} alt="" />
          ) : (
            <>
              <i className="pi pi-image" />
              <span>Elegir imagen</span>
            </>
          )}
        </button>
        <label className="chat-stories-field">
          Texto opcional
          <InputTextarea
            value={uploadText}
            onChange={(e) => setUploadText(e.target.value)}
            rows={3}
            maxLength={140}
            autoResize
            className="w-full"
            placeholder="Aviso corto para la comunidad…"
          />
        </label>
      </Dialog>
    </div>
  );
}
